import React, { useEffect, useState } from "react";

import { Modal, Typography } from "@packages/ds";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import { ENVIRONMENT_TYPES } from "Constants/constants";
import { Button, ButtonWrapper, BackLink, IconControl } from "ds/Button";
import Checkbox from "ds/Checkbox";
import Dialog from "ds/Dialog";
import Input from "ds/Input";
import Loading from "ds/PageLoading";
import { Tooltip } from "ds/Tooltip";
import useDecodedParams from "Hooks/useDecodedParams";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import { arrayIsDeepEqual, capitalize } from "Libs/utils";
import { selectCurrentUserUUID } from "Reducers/app/selectors";
import {
  gitProjectSelector,
  selectHasManagementAccess
} from "Reducers/project/project";
import { useDeleteProjectUserAccess } from "Reducers/userAccess/hooks/useDeleteProjectUserAccess";
import { useUpdateProjectUserAccess } from "Reducers/userAccess/hooks/useUpdateProjectUserAccess";
import { useUserAccessByUserId } from "Reducers/userAccess/hooks/useUserAccessByUserId";
import useOrganizationFromURL from "src/organization/pages/settings/pages/members/hooks/useOrganizationFromURL";
import { useAppSelector } from "Store/hooks";

import EnvironmentTypeRow from "../Common/EnvironmentTypeRow";
import { LeaveProjectModal } from "../Common/LeaveProjectModal";

import * as S from "./UserDetails.styles";

import type {
  ProjectAccessEnvironmentType,
  EnvironmentAccessRole
} from "@packages/client";
import type { EnvironmentTypeAccess } from "Api/organizations/member/projectAccess/ProjectAccess.types";

const toggleAccess = (
  accesses: Array<EnvironmentTypeAccess>,
  environmentType: ProjectAccessEnvironmentType,
  accessLevel?: EnvironmentAccessRole
): Array<EnvironmentTypeAccess> => {
  const filteredValues = accesses.filter(
    access => !access.startsWith(`${environmentType}:`)
  );

  return accessLevel
    ? [...filteredValues, `${environmentType}:${accessLevel}`]
    : filteredValues;
};

const getAccessLevelFromValue = (
  environmentType: ProjectAccessEnvironmentType,
  value: Array<EnvironmentTypeAccess>
) =>
  value
    .map(access => access.split(":"))
    .find(([envType]) => envType === environmentType)?.[1] as
    | EnvironmentAccessRole
    | undefined;

const UserDetails = () => {
  const intl = useIntl();
  const navigate = useNavigate();

  const {
    projectId = "",
    organizationId = "",
    userId
  } = useDecodedParams<{
    organizationId: string;
    projectId: string;
    userId: string;
  }>();
  const [
    removeUserConfirmationModalIsOpen,
    setRemoveUserConfirmationModalIsOpen
  ] = useState(false);
  const [isLeaveProjectActive, setIsLeaveProjectActive] = useState(false);

  const organization = useOrganizationFromURL(organizationId);

  const [deleteProjectUserAccess] = useDeleteProjectUserAccess();
  const [updateProjectUserAccess, isSubmitting] = useUpdateProjectUserAccess();
  const [user, isLoadingUser] = useUserAccessByUserId(projectId, userId);

  const manageAccessEnabled = useSelectorWithUrlParams(
    selectHasManagementAccess
  );
  const loggedInUserId = useAppSelector(selectCurrentUserUUID);
  const isOwner = organization?.owner_id === user?.id;
  const isLoggedInUser = user?.id === loggedInUserId;

  const [permissions, setPermissions] = useState<Array<EnvironmentTypeAccess>>(
    []
  );

  // UI State
  const hasFormBeenEdited = !arrayIsDeepEqual(
    user?.permissions || [],
    permissions
  );

  useEffect(() => {
    if (user) setPermissions(user.permissions);
  }, [user]);

  const isProjectAdmin = permissions.includes("admin");
  const accessListURL = `/${organizationId}/${projectId}/-/settings/access`;
  const showProjectPermissions = isProjectAdmin;
  const showEnvironmentPermissions = !showProjectPermissions;
  const isSubmitDisabled =
    isSubmitting ||
    permissions.filter(p => p !== "viewer" && !p.includes("no-access"))
      .length === 0;

  const project = useSelectorWithUrlParams(gitProjectSelector);
  const handleProjectAdminChecked = () => {
    if (!user) return;
    setPermissions(
      isProjectAdmin
        ? // If the user is not originally and admin we can reset the environment
          // type permissions just in case the user made a mistake
          user.permissions.includes("admin")
          ? []
          : user.permissions
        : ["admin"]
    );
  };

  const handleRemoveUser = () => setRemoveUserConfirmationModalIsOpen(true);

  const removeUser = async () => {
    if (!user) return;
    await deleteProjectUserAccess(projectId, user.id);
    navigate(accessListURL);
  };

  const handleSave = async () => {
    if (!user) return;
    await updateProjectUserAccess(
      projectId,
      user.id,
      // We can't send `no-access` permissions to the backend
      permissions.filter(p => !p.includes("no-access"))
    );
    navigate(accessListURL);
  };

  if (isLoadingUser) {
    return <Loading />;
  }

  if (!user) {
    return null;
  }

  return (
    <section>
      <BackLink from="access" to={accessListURL} capitalized={false} />
      <Typography.Title tag="h3">{user.name}</Typography.Title>

      <S.Container>
        <Input
          analyticId="email"
          id="email"
          label={intl.formatMessage({ id: "email" })}
          value={user.email}
          disabled
          aria-disabled
        />
        <>
          <Typography.Title tag="h4" style={{ margin: "16px 0" }}>
            {intl.formatMessage({
              id: "access.project.permissions"
            })}
          </Typography.Title>
          <Checkbox
            label={`${intl.formatMessage({ id: "access.project_admin" })}`}
            checked={isProjectAdmin}
            disabled={!manageAccessEnabled || isOwner}
            aria-disabled={!manageAccessEnabled || isOwner}
            onCheckedChanged={handleProjectAdminChecked}
            forId="super-user"
            id="access.project_admin"
          />
        </>
        {showEnvironmentPermissions && (
          <>
            <S.EnvironmentPermissionsWrapper>
              <Typography.Title tag="h4" style={{ margin: "16px 16px 16px 0" }}>
                {intl.formatMessage({
                  id: "invitation.environment_types.permission"
                })}
              </Typography.Title>
              <Dialog
                align="start"
                description={intl.formatMessage({
                  id: "access.environment_types.description"
                })}
                analyticId="access.environment_types.description"
                link={
                  <a
                    href={intl.formatMessage({
                      id: "links.documentation.users"
                    })}
                  >
                    {intl.formatMessage({ id: "learnmore" })}
                  </a>
                }
                trigger={
                  <IconControl>
                    <S.InfoIcon />
                  </IconControl>
                }
              />
            </S.EnvironmentPermissionsWrapper>
            <S.EnvironmentTypesTable>
              <S.EnvironmentTableHeaders>
                <S.EnvironmentTableHeader width="20%">
                  <Typography.Label>
                    {intl.formatMessage({
                      id: "invitation.environment_types"
                    })}
                  </Typography.Label>
                </S.EnvironmentTableHeader>
                <S.EnvironmentTableHeader width="35%">
                  <Typography.Label>
                    {intl.formatMessage({
                      id: "invitation.environment.type"
                    })}
                  </Typography.Label>
                </S.EnvironmentTableHeader>
                <S.EnvironmentTableHeader width="45%">
                  <Typography.Label>
                    {intl.formatMessage({
                      id: "invitation.table.environments"
                    })}
                  </Typography.Label>
                </S.EnvironmentTableHeader>
              </S.EnvironmentTableHeaders>
              {ENVIRONMENT_TYPES.map(environmentType => (
                <EnvironmentTypeRow
                  key={environmentType}
                  environmentType={environmentType}
                  value={getAccessLevelFromValue(environmentType, permissions)}
                  onChange={selectedValue =>
                    setPermissions(
                      toggleAccess(
                        permissions,
                        environmentType,
                        selectedValue?.value
                      )
                    )
                  }
                  disabled={!manageAccessEnabled || isOwner}
                />
              ))}
            </S.EnvironmentTypesTable>
          </>
        )}

        <div
          style={{
            marginTop: "40px",
            display: "flex",
            justifyContent: hasFormBeenEdited ? "space-between" : "flex-end"
          }}
        >
          {hasFormBeenEdited && (
            <ButtonWrapper justifyContent="end">
              <Button
                disabled={isSubmitDisabled}
                analyticId="save"
                onClick={handleSave}
              >
                {capitalize(intl.formatMessage({ id: "save" }))}
              </Button>
              <Button
                variant="secondary"
                analyticId="cancel"
                to={accessListURL}
              >
                {capitalize(intl.formatMessage({ id: "cancel" }))}
              </Button>
            </ButtonWrapper>
          )}
          {manageAccessEnabled ? (
            <ButtonWrapper justifyContent="end">
              {isOwner ? (
                <Tooltip
                  tooltip={intl.formatMessage({
                    id: "project.access.owner.tooltip"
                  })}
                  align="end"
                  analyticId="project.access.owner.tooltip"
                >
                  <Button
                    analyticId="project.access.remove_user"
                    id={`user-remove-${user.id}`}
                    variant="outline"
                    onClick={handleRemoveUser}
                    disabled={true}
                  >
                    {intl.formatMessage({ id: "project.access.remove_user" })}
                  </Button>
                </Tooltip>
              ) : (
                <Button
                  id={`user-remove-${user.id}`}
                  variant="outline"
                  onClick={handleRemoveUser}
                  analyticId="project.access.remove_user"
                >
                  {intl.formatMessage({ id: "project.access.remove_user" })}
                </Button>
              )}
            </ButtonWrapper>
          ) : (
            isLoggedInUser && (
              <ButtonWrapper>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsLeaveProjectActive(true);
                  }}
                  analyticId="project.access.leave"
                >
                  {intl.formatMessage({ id: "project.access.leave" })}
                </Button>
              </ButtonWrapper>
            )
          )}
        </div>
      </S.Container>
      <RemoveUserModal
        isOpen={removeUserConfirmationModalIsOpen}
        setIsOpen={setRemoveUserConfirmationModalIsOpen}
        email={user.email}
        onAccept={removeUser}
      />
      {isLeaveProjectActive && (
        <LeaveProjectModal
          organizationId={organizationId}
          onSuccess={() => navigate(`/${organizationId}`)}
          projectId={projectId}
          projectName={project?.title || ""}
          userId={userId}
          userName={intl.formatMessage({ id: "project.access.yourself" })}
          fullUserName={user.name}
          onClose={() => {
            setIsLeaveProjectActive(false);
          }}
        />
      )}
    </section>
  );
};

function RemoveUserModal({ email, isOpen, setIsOpen, onAccept }) {
  const intl = useIntl();
  const [isDisabled, setIsDisabled] = useState(false);

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={() => {
        setIsOpen(false);
      }}
      handleClose={() => {
        setIsOpen(false);
      }}
      id="remove-user-modal"
    >
      <Typography.Title tag="h2">
        {intl.formatMessage({ id: "access.removeUserModalTitle" })}
      </Typography.Title>
      <Typography.BodyText>
        {intl.formatMessage({
          id: "access.removeUserModalBody"
        })}
        {" " + email}
      </Typography.BodyText>
      <ButtonWrapper spacing="modal" justifyContent="end">
        <Button
          variant="outline"
          onClick={() => {
            setIsOpen(false);
          }}
          analyticId="cancel"
        >
          {intl.formatMessage({ id: "cancel" })}
        </Button>
        <Button
          disabled={isDisabled}
          onClick={() => {
            setIsDisabled(true);
            onAccept();
          }}
          analyticId="accept"
        >
          {intl.formatMessage({ id: "accept" })}
        </Button>
      </ButtonWrapper>
    </Modal>
  );
}

export default UserDetails;
